import { Progress } from "@/components/ui/progress"
import { Typography } from "@/components/ui/typography"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUserLimits from "@/hooks/useUserLimits"
import { LOCALES } from "./locales"

/** Credits count per calendar month in UTC (backend `get_start_of_period`). */
export const nextCreditReset = (now: Date): Date =>
  new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))

/** The organization's credits of the month. Renders nothing without a credit
 *  limit: Pro, contracts and trials have none. */
export const CreditsMeter: React.FC = () => {
  const { translate, currentLanguage } = useTranslation()
  const { creditsLimit, creditsUsage } = useUserLimits()
  if (!creditsLimit) return null

  const resetsOn = new Intl.DateTimeFormat(
    LOCALES[currentLanguage] ?? "en-GB",
    { day: "numeric", month: "long", timeZone: "UTC" },
  ).format(nextCreditReset(new Date()))

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="credits-meter"
      data-exhausted={creditsUsage >= creditsLimit}
    >
      <Typography as="small">
        {translate("CREDITS/USED", { used: creditsUsage, limit: creditsLimit })}
      </Typography>
      <Progress
        value={Math.min(100, (creditsUsage / creditsLimit) * 100)}
        aria-label={translate("CREDITS/USED", {
          used: creditsUsage,
          limit: creditsLimit,
        })}
      />
      <Typography as="muted">
        {translate("CREDITS/RESETS", { date: resetsOn })}
      </Typography>
    </div>
  )
}
